import React, { useState, useEffect } from 'react';
import {
  Bell, Mail, Send, CheckCircle2, XCircle, Clock,
  Newspaper, Trophy, Flag, BarChart3, AlertCircle, Zap
} from 'lucide-react';
import { COLORS } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import {
  sendTestNotification, sendWeeklyDigest, sendScoreChangeNotification,
  sendGoalReminder, fetchNotificationHistory, fetchNotificationStatus
} from '../services/api';

const TYPE_META = {
  test: { icon: Mail, color: '#5C6BC0', label: 'Test' },
  weekly_digest: { icon: Newspaper, color: '#6B8E23', label: 'Weekly Digest' },
  score_change: { icon: BarChart3, color: '#FF9800', label: 'Score Change' },
  goal_reminder: { icon: Flag, color: '#4CAF50', label: 'Goal Reminder' },
  challenge_update: { icon: Trophy, color: '#E91E63', label: 'Challenge' },
  general: { icon: Bell, color: COLORS.textSecondary, label: 'General' },
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    Promise.all([fetchNotificationHistory(), fetchNotificationStatus()])
      .then(([hist, stat]) => { setHistory(hist); setStatus(stat); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleAction = async (type, fn) => {
    setSending(type);
    setMessage('');
    try {
      const result = await fn();
      setMessage(`${type === 'test' ? 'Test notification' : type.replace('_', ' ')} ${result.status === 'delivered_mock' ? 'logged (mock mode)' : 'sent'}!`);
      const hist = await fetchNotificationHistory();
      setHistory(hist);
      const stat = await fetchNotificationStatus();
      setStatus(stat);
    } catch (err) {
      setMessage(err.response?.data?.detail || err.response?.data?.message || 'Failed to send');
    } finally {
      setSending('');
    }
  };

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: COLORS.textSecondary }}>Loading notifications...</div>;

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', padding: '16px 24px 32px' }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, margin: 0, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>Email Notifications</h1>

      {/* Status Banner */}
      {status && (
        <div style={{
          backgroundColor: status.mock_mode ? '#FFF3E0' : '#E8F5E9',
          borderRadius: 12, padding: 16, marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 20,
            backgroundColor: status.mock_mode ? '#FFE0B2' : '#C8E6C9',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {status.mock_mode ? <AlertCircle size={22} color="#F57C00" /> : <CheckCircle2 size={22} color="#388E3C" />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>
              {status.mock_mode ? 'Mock Mode Active' : 'SendGrid Connected'}
            </div>
            <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>
              {status.mock_mode
                ? 'Emails are logged to database (no real emails sent). Add SENDGRID_API_KEY to activate.'
                : `${status.successful} emails sent successfully`}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>{status.total_sent}</div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary }}>Total Sent</div>
          </div>
        </div>
      )}

      {message && (
        <div style={{ backgroundColor: '#E8F5E9', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={16} color={COLORS.success} />
          <span style={{ fontSize: 14, color: COLORS.success, fontWeight: 500 }}>{message}</span>
        </div>
      )}

      {/* Send Actions */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>Send Notifications</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {[
            { type: 'test', label: 'Test Email', desc: 'Send a test notification to verify setup', icon: Mail, color: '#5C6BC0', fn: () => sendTestNotification(user?.email || '', 'ThriveLonger.ai Test') },
            { type: 'weekly_digest', label: 'Weekly Digest', desc: 'Send your weekly health summary', icon: Newspaper, color: '#6B8E23', fn: sendWeeklyDigest },
            { type: 'score_change', label: 'Score Alert', desc: 'Simulate a health score change alert', icon: BarChart3, color: '#FF9800', fn: sendScoreChangeNotification },
            { type: 'goal_reminder', label: 'Goal Reminder', desc: 'Send a reminder for active goals', icon: Flag, color: '#4CAF50', fn: sendGoalReminder },
          ].map(action => (
            <button
              key={action.type}
              onClick={() => handleAction(action.type, action.fn)}
              disabled={!!sending}
              style={{
                flex: '1 1 calc(50% - 5px)', minWidth: 220, display: 'flex', alignItems: 'center', gap: 12,
                backgroundColor: COLORS.surface, borderRadius: 12, padding: 16, border: 'none',
                cursor: sending ? 'default' : 'pointer', textAlign: 'left',
                opacity: sending === action.type ? 0.7 : 1,
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              className="goal-card"
            >
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                backgroundColor: `${action.color}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {sending === action.type ? (
                  <RefreshIcon color={action.color} />
                ) : (
                  <action.icon size={22} color={action.color} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{action.label}</div>
                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{action.desc}</div>
              </div>
              <Send size={16} color={COLORS.textSecondary} />
            </button>
          ))}
        </div>
      </div>

      {/* Notification History */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 12, fontFamily: 'Outfit, sans-serif' }}>
          Notification History
          {history.length > 0 && <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.textSecondary, marginLeft: 8 }}>({history.length})</span>}
        </h2>
        {history.length === 0 ? (
          <div style={{ textAlign: 'center', backgroundColor: COLORS.surface, borderRadius: 16, padding: '40px 24px' }}>
            <Bell size={48} color={COLORS.border} />
            <p style={{ fontSize: 16, color: COLORS.textSecondary, marginTop: 12 }}>No notifications sent yet</p>
          </div>
        ) : (
          history.map((notif, i) => {
            const meta = TYPE_META[notif.notification_type] || TYPE_META.general;
            const Icon = meta.icon;
            const isSuccess = notif.status === 'sent' || notif.status === 'delivered_mock';
            return (
              <div key={notif.email_id || i} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                backgroundColor: COLORS.surface, borderRadius: 8, padding: 14, marginBottom: 6,
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${meta.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={18} color={meta.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>{notif.subject || meta.label}</div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>
                    To: {notif.to_email} · {new Date(notif.created_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  {isSuccess ? (
                    <>
                      <CheckCircle2 size={14} color={COLORS.success} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.success }}>
                        {notif.mock_mode ? 'Mock' : 'Sent'}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle size={14} color={COLORS.error} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.error }}>Failed</span>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function RefreshIcon({ color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" /><path d="M21 21v-5h-5" />
    </svg>
  );
}
